/** Merge a completed person report back into rerun/edit intake. */

export type ResearchRerunKeyData = {
  names: string;
  addresses: string;
  phones: string;
  emails: string;
  birthdate: string;
  ageRange: string;
  ssn: string;
  dlNumber: string;
  companies: string;
  socialMediaHandles: string;
  website: string;
  knownAliases: string;
};

const EMPTY: ResearchRerunKeyData = {
  names: '',
  addresses: '',
  phones: '',
  emails: '',
  birthdate: '',
  ageRange: '',
  ssn: '',
  dlNumber: '',
  companies: '',
  socialMediaHandles: '',
  website: '',
  knownAliases: '',
};

const firstNonEmpty = (...values: string[]): string => {
  for (const value of values) {
    if (String(value || '').trim()) return String(value).trim();
  }
  return '';
};

const csvJoin = (values: unknown, fallback = ''): string => {
  if (Array.isArray(values)) {
    const joined = values.map((row) => String(row || '').trim()).filter(Boolean).join(', ');
    if (joined) return joined;
  }
  return String(fallback || '').trim();
};

export const socialHandleValues = (handles: unknown): string[] => {
  if (!handles) return [];
  if (typeof handles === 'string') {
    return String(handles)
      .split(/[,;]+/)
      .map((row) => row.trim())
      .filter((row) => /https?:\/\//i.test(row) || /(?:facebook|instagram|linkedin|tiktok)\.com/i.test(row));
  }
  if (typeof handles !== 'object') return [];
  const out: string[] = [];
  for (const value of Object.values(handles as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim()) out.push(value.trim());
    else if (value && typeof value === 'object') {
      const url = String((value as { url?: string }).url || '').trim();
      if (url) out.push(url);
    }
  }
  return out;
};

export const mergeResearchReportKeyData = (report: {
  targetName?: string;
  website?: string;
  knownAliases?: string[];
  keyData?: Partial<ResearchRerunKeyData> | null;
  factDatabase?: { entity?: Record<string, unknown> } | null;
} | null): ResearchRerunKeyData => {
  if (!report) return { ...EMPTY };
  const key = report.keyData || {};
  const entity = (report.factDatabase?.entity || {}) as Record<string, unknown>;
  const factSocial = socialHandleValues(entity.social_handles);
  return {
    names: firstNonEmpty(String(key.names || ''), String(entity.canonical_name || ''), String(report.targetName || '')),
    website: firstNonEmpty(String(key.website || ''), String(report.website || '')),
    knownAliases: firstNonEmpty(
      String(key.knownAliases || ''),
      csvJoin(entity.alt_names),
      Array.isArray(report.knownAliases) ? report.knownAliases.filter(Boolean).join(', ') : '',
    ),
    addresses: firstNonEmpty(String(key.addresses || ''), csvJoin(entity.addresses)),
    phones: firstNonEmpty(String(key.phones || ''), csvJoin(entity.phones)),
    emails: firstNonEmpty(String(key.emails || ''), csvJoin(entity.emails)),
    birthdate: firstNonEmpty(String(key.birthdate || ''), String(entity.birthdate || '')),
    ageRange: firstNonEmpty(String(key.ageRange || ''), String(entity.age_range || '')),
    ssn: firstNonEmpty(String(key.ssn || ''), String(entity.ssn || '')),
    dlNumber: firstNonEmpty(String(key.dlNumber || ''), String(entity.dl_number || '')),
    companies: firstNonEmpty(String(key.companies || ''), csvJoin(entity.company_affiliations)),
    socialMediaHandles: firstNonEmpty(String(key.socialMediaHandles || ''), factSocial.join(', ')),
  };
};

export const mergeResearchReportKnownAliases = (report: {
  knownAliases?: string[];
  keyData?: Partial<ResearchRerunKeyData> | null;
  factDatabase?: { entity?: Record<string, unknown> } | null;
} | null): string[] => {
  const merged = mergeResearchReportKeyData(report);
  const fromReport = Array.isArray(report?.knownAliases) ? report.knownAliases.filter(Boolean) : [];
  if (fromReport.length) return fromReport;
  return merged.knownAliases.split(',').map((row) => row.trim()).filter(Boolean);
};
